/* Settings sections that used to say "open the classic dashboard"
   (2026-10-28): the apps' switches, the admin team, and connected devices.
   The writes are the classic dashboard's own (BusTracking → FleetAdminSettings):
   two allow-listed org fields, and the admin invite (pendingAdminInvites +
   the branded invite email). */
import React, { useState } from 'react';
import { arrayUnion, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { adminInviteActionSettings } from '../../components/selfServeSignup';
import { addonStatusLabel, OrgAddon } from '../../components/AddonConnector';
import { sendAdminInviteEmailApi } from '../../services/paymentsApi';
import { SUPPORT_WHATSAPP_HREF } from '../../utils/companyContact';
import { IcAlert, useAction } from '../kit';
import type { FleetModel } from '../data/model';

/** The console's on/off switch: 44 × 26, teal when on. */
export const Switch: React.FC<{ on: boolean; onChange: () => void; label: string; busy?: boolean }> = ({ on, onChange, label, busy }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={busy}
    onClick={onChange}
    style={{
      width: 44, height: 26, flex: 'none', borderRadius: 999, border: 0, padding: 3, cursor: busy ? 'default' : 'pointer',
      background: on ? 'var(--teal)' : 'var(--s3)', opacity: busy ? 0.6 : 1, transition: 'background-color 160ms',
      display: 'flex', justifyContent: on ? 'flex-end' : 'flex-start',
    }}
  >
    <span style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(17,21,32,.25)' }} />
  </button>
);

const Row: React.FC<{ title: string; body: string; children: React.ReactNode; last?: boolean }> = ({ title, body, children, last }) => (
  <div className="row" style={{ padding: '14px 20px', gap: 16, borderBottom: last ? undefined : '1px solid var(--line)' }}>
    <div style={{ flex: 1, minWidth: 0 }}>
      <b style={{ display: 'block', fontSize: 15 }}>{title}</b>
      <span className="sm" style={{ color: 'var(--muted)' }}>{body}</span>
    </div>
    {children}
  </div>
);

/** Flip one allow-listed org field (the rules let a fleet admin write
 *  parentMapEnabled and legacyFuelFlowEnabled). No optimistic flip: the org
 *  listener moves the switch once the write is on the doc. */
function useOrgFlag(f: FleetModel) {
  const action = useAction();
  const [busy, setBusy] = useState<string | null>(null);
  const flip = (field: 'parentMapEnabled' | 'legacyFuelFlowEnabled', next: boolean, done: string) => {
    if (!f.org.docId || busy) return;
    setBusy(field);
    void action({
      busy: 'Saving…',
      done,
      fail: 'Couldn’t save that. Try again in a minute.',
      run: () => updateDoc(doc(db, 'organizations', f.org.docId as string), { [field]: next, updatedAt: serverTimestamp() }),
    }).finally(() => setBusy(null));
  };
  return { busy, flip };
}

/* ── Apps: what parents see, how drivers log fuel ──────────────────────── */
export const AppsPanel: React.FC<{ f: FleetModel }> = ({ f }) => {
  const { busy, flip } = useOrgFlag(f);
  const parentMap = f.org.raw.parentMapEnabled !== false; // absent = on
  const legacyFuel = f.org.raw.legacyFuelFlowEnabled === true; // absent = off
  return (
    <section className="panel">
      <div className="ph">
        <div>
          <h2 className="h2">Parent and driver apps</h2>
          <p className="sm" style={{ margin: '2px 0 0', color: 'var(--muted)' }}>Changes reach every phone the next time the app opens.</p>
        </div>
      </div>
      <Row title="Parents see the bus on a live map" body={parentMap ? 'On — parents follow the bus while it is on a run.' : 'Off — parents get the alerts, but no live map.'}>
        <Switch on={parentMap} busy={busy === 'parentMapEnabled'} label="Parents see the bus on a live map"
          onChange={() => flip('parentMapEnabled', !parentMap, parentMap ? 'Parents no longer see the live map' : 'Parents can see the live map')} />
      </Row>
      <Row last title="Drivers log fuel the old way" body={legacyFuel ? 'On — drivers use the older fuel form.' : 'Off — drivers log fuel with the refuel trip.'}>
        <Switch on={legacyFuel} busy={busy === 'legacyFuelFlowEnabled'} label="Drivers log fuel the old way"
          onChange={() => flip('legacyFuelFlowEnabled', !legacyFuel, legacyFuel ? 'Drivers use the refuel trip' : 'Drivers use the older fuel form')} />
      </Row>
    </section>
  );
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const strList = (v: unknown) => (Array.isArray(v) ? v : []).map((e) => String(e || '').toLowerCase()).filter(Boolean);

/* ── Team: the school's admins, and inviting another ───────────────────── */
export const TeamPanel: React.FC<{ f: FleetModel }> = ({ f }) => {
  const action = useAction();
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  const [sending, setSending] = useState<string | null>(null);

  const primary = String(f.org.raw.adminEmail || '').toLowerCase();
  const admins = strList(f.org.raw.adminEmails).filter((e) => e !== primary);
  const pending = strList(f.org.raw.pendingAdminInvites).filter((e) => e !== primary && !admins.includes(e));

  /* The classic dashboard's sendAdminInviteTo: name the address on the org
     (non-fatal if refused), then send the branded invite email. */
  const invite = (addr: string, resend: boolean) => {
    if (!f.org.code || sending) return;
    setSending(addr);
    void action({
      busy: resend ? `Sending the invite to ${addr} again…` : `Inviting ${addr}…`,
      done: resend ? `Invite sent again to ${addr}` : `Invite sent to ${addr}`,
      fail: `Couldn’t send the invite to ${addr}. Try again in a minute.`,
      run: async () => {
        if (f.org.docId) {
          try {
            await updateDoc(doc(db, 'organizations', f.org.docId), { pendingAdminInvites: arrayUnion(addr), updatedAt: serverTimestamp() });
          } catch { /* the email still goes out */ }
        }
        const r = await sendAdminInviteEmailApi({ orgCode: f.org.code, email: addr, continueUrl: adminInviteActionSettings(f.org.code).url });
        if (!r.ok) throw new Error(r.error || 'invite failed');
        if (!resend) { setEmail(''); setTouched(false); }
      },
    }).finally(() => setSending(null));
  };

  const addr = email.trim().toLowerCase();
  const err = touched && !EMAIL_RE.test(addr)
    ? 'Enter an email address.'
    : touched && (addr === primary || admins.includes(addr)) ? 'That address is already an admin.' : null;

  const person = (e: string, tag: string, action?: React.ReactNode) => (
    <div key={e} className="row" style={{ padding: '12px 20px', gap: 12, borderBottom: '1px solid var(--line)' }}>
      <span className="av" style={{ width: 32, height: 32, background: 'var(--s2)', color: 'var(--ink2)', fontSize: 13 }}>{e.slice(0, 2).toUpperCase()}</span>
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{e}</span>
      <span className="sm" style={{ color: 'var(--muted)' }}>{tag}</span>
      {action}
    </div>
  );

  return (
    <section className="panel">
      <div className="ph">
        <div>
          <h2 className="h2">Admins</h2>
          <p className="sm" style={{ margin: '2px 0 0', color: 'var(--muted)' }}>People who can open this console. Drivers and parents are invited from Drivers and parents.</p>
        </div>
      </div>
      {primary && person(primary, 'Owner')}
      {admins.map((e) => person(e, 'Admin'))}
      {pending.map((e) => person(e, 'Invited', (
        <button type="button" className="btn s b-line" disabled={!!sending} onClick={() => invite(e, true)}>{sending === e ? 'Sending…' : 'Send again'}</button>
      )))}
      <form
        onSubmit={(ev) => { ev.preventDefault(); setTouched(true); if (EMAIL_RE.test(addr) && addr !== primary && !admins.includes(addr)) invite(addr, false); }}
        style={{ padding: '16px 20px', display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}
      >
        <div style={{ flex: 1, minWidth: 220, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <input className={'tin' + (err ? ' ferr' : '')} type="email" placeholder="colleague@school.ac.ke" value={email} aria-label="Admin email"
            aria-invalid={!!err} onChange={(e) => setEmail(e.target.value)} onBlur={() => setTouched(email.trim() !== '')} />
          {err && <span className="ferr-msg"><IcAlert size={15} />{err}</span>}
        </div>
        <button type="submit" className="btn b-ink" disabled={!!sending}>{sending === addr ? 'Inviting…' : 'Invite admin'}</button>
      </form>
    </section>
  );
};

const DEVICES: { key: 'gps' | 'fuel' | 'fees'; name: string; what: string }[] = [
  { key: 'gps', name: 'GPS trackers', what: 'A tracker in the bus instead of the driver’s phone.' },
  { key: 'fuel', name: 'Fuel sensors', what: 'Litres read from the tank, compared with what drivers log.' },
  { key: 'fees', name: 'School fees system', what: 'Unpaid fees from your fees system, for Zana and the bus list.' },
];

/* ── Connected devices: status only — Trasse connects them ─────────────── */
export const DevicesPanel: React.FC<{ f: FleetModel }> = ({ f }) => {
  const addons = (f.org.raw.addons || {}) as Partial<Record<'gps' | 'fuel' | 'fees', OrgAddon>>;
  const orgName = f.org.name || f.org.code;
  return (
    <section className="panel">
      <div className="ph">
        <div>
          <h2 className="h2">Connected devices</h2>
          <p className="sm" style={{ margin: '2px 0 0', color: 'var(--muted)' }}>Trasse connects these for you. Without them the app uses the driver’s phone and what drivers log.</p>
        </div>
      </div>
      {DEVICES.map((d, i) => {
        const a = addons[d.key];
        const on = a?.status === 'connected';
        return (
          <Row key={d.key} last={i === DEVICES.length - 1} title={d.name} body={a?.wanted ? addonStatusLabel(a.status, a.verified) : d.what}>
            {on
              ? <span className={'chip ' + (a?.verified ? 'c-ok' : 'c-teal')}>{a?.verified ? 'Connected' : 'Testing'}</span>
              : (
                <a className="btn s b-line" target="_blank" rel="noreferrer"
                  href={`${SUPPORT_WHATSAPP_HREF}?text=${encodeURIComponent(`We would like to connect ${d.name.toLowerCase()} for ${orgName} (${f.org.code}).`)}`}>
                  Ask Trasse
                </a>
              )}
          </Row>
        );
      })}
    </section>
  );
};
